// Primary 3 Chinese Data - Part 1
// Based on Primary Chinese P3 Topics.txt

export const p3DataPart1 = {
  title: "小學三年級中文學習",
  description: "專為小學三年級學生設計的中文學習內容，進一步提升語文能力和寫作技巧",
  objectives: "提升語文運用能力，培養寫作思維，掌握更複雜的語法結構",
  focus: "重點學習分號、字形辨識、近義詞等進階語法，以及更豐富的文體閱讀理解",
  
  readingTypes: [
    "記人", "海報", "電郵", "報道", "遊記", "描寫動物",
    "童話故事", "說明事物", "描寫植物", "記事", "寓言故事", "網頁"
  ],
  
  languageUsage: [
    {
      title: "分號",
      description: "學習分號的用法，掌握複雜句子的標點符號",
      sampleQuestions: [
        {
          question: "選擇正確的標點符號：我喜歡讀書（ ）他喜歡運動。",
          answer: "；",
          explanation: "分號用於分隔兩個相關但獨立的句子。"
        },
        {
          question: "選擇正確的標點符號：春天來了（ ）花兒開了。",
          answer: "；",
          explanation: "分號用於分隔兩個並列的句子。"
        },
        {
          question: "選擇正確的標點符號：小明很聰明（ ）小華很勤奮。",
          answer: "；",
          explanation: "分號用於分隔兩個對比的句子。"
        },
        {
          question: "選擇正確的標點符號：我喜歡紅色（ ）他喜歡藍色。",
          answer: "；",
          explanation: "分號用於分隔兩個選擇的句子。"
        },
        {
          question: "選擇正確的標點符號：我喜歡讀書（ ）他喜歡畫畫。",
          answer: "；",
          explanation: "分號用於分隔兩個並列的句子。"
        }
      ]
    },
    {
      title: "字形辨識",
      description: "學習字形的辨識，掌握漢字的結構特點",
      sampleQuestions: [
        {
          question: "選擇正確的字：我喜歡（ ）書。",
          answer: "讀",
          explanation: "『讀』是讀書的意思，注意字形結構。"
        },
        {
          question: "選擇正確的字：這（ ）花很漂亮。",
          answer: "朵",
          explanation: "『朵』是量詞，注意字形特點。"
        },
        {
          question: "選擇正確的字：我（ ）家吃飯。",
          answer: "回",
          explanation: "『回』是回家的意思，注意字形。"
        },
        {
          question: "選擇正確的字：這（ ）書很有趣。",
          answer: "本",
          explanation: "『本』是量詞，注意字形結構。"
        },
        {
          question: "選擇正確的字：我（ ）媽媽去買菜。",
          answer: "跟",
          explanation: "『跟』是跟隨的意思，注意字形。"
        }
      ]
    },
    {
      title: "近義詞",
      description: "學習近義詞的區別，掌握詞語的細微差異",
      sampleQuestions: [
        {
          question: "選擇正確的近義詞：『美麗』的近義詞是（ ）。",
          answer: "漂亮",
          explanation: "『美麗』和『漂亮』都是形容外表好看的詞語。"
        },
        {
          question: "選擇正確的近義詞：『快樂』的近義詞是（ ）。",
          answer: "開心",
          explanation: "『快樂』和『開心』都是形容心情愉快的詞語。"
        },
        {
          question: "選擇正確的近義詞：『聰明』的近義詞是（ ）。",
          answer: "智慧",
          explanation: "『聰明』和『智慧』都是形容智力高的詞語。"
        },
        {
          question: "選擇正確的近義詞：『勤奮』的近義詞是（ ）。",
          answer: "努力",
          explanation: "『勤奮』和『努力』都是形容工作認真的詞語。"
        },
        {
          question: "選擇正確的近義詞：『安靜』的近義詞是（ ）。",
          answer: "寧靜",
          explanation: "『安靜』和『寧靜』都是形容環境安靜的詞語。"
        }
      ]
    }
  ]
};

// Primary 3 Chinese Data - Part 2
export const p3DataPart2 = {
  languageUsage: [
    {
      title: "動詞重量",
      description: "學習動詞的重疊用法，掌握動詞的語法特點",
      sampleQuestions: [
        {
          question: "選擇正確的動詞重疊：我（ ）地讀書。",
          answer: "認真認真",
          explanation: "動詞重疊表示動作的重複或強調。"
        },
        {
          question: "選擇正確的動詞重疊：他（ ）地工作。",
          answer: "努力努力",
          explanation: "動詞重疊表示動作的持續。"
        },
        {
          question: "選擇正確的動詞重疊：她（ ）地唱歌。",
          answer: "輕輕",
          explanation: "動詞重疊表示動作的輕柔。"
        },
        {
          question: "選擇正確的動詞重疊：我們（ ）地學習。",
          answer: "好好",
          explanation: "動詞重疊表示動作的認真。"
        },
        {
          question: "選擇正確的動詞重疊：他（ ）地走路。",
          answer: "慢慢",
          explanation: "動詞重疊表示動作的緩慢。"
        }
      ]
    },
    {
      title: "標點符號",
      description: "學習各種標點符號的用法，掌握標點符號的語法功能",
      sampleQuestions: [
        {
          question: "選擇正確的標點符號：我喜歡讀書（ ）",
          answer: "。",
          explanation: "句號用於表示句子的結束。"
        },
        {
          question: "選擇正確的標點符號：你喜歡什麼顏色（ ）",
          answer: "？",
          explanation: "問號用於表示疑問句。"
        },
        {
          question: "選擇正確的標點符號：哇（ ）真漂亮（ ）",
          answer: "！",
          explanation: "感嘆號用於表示感嘆句。"
        },
        {
          question: "選擇正確的標點符號：我喜歡紅色（ ）藍色（ ）綠色。",
          answer: "、",
          explanation: "頓號用於分隔並列的詞語。"
        },
        {
          question: "選擇正確的標點符號：他說（ ）我喜歡讀書（ ）",
          answer: "：",
          explanation: "冒號用於表示引語的開始。"
        }
      ]
    }
  ]
}; 